import React from "react";
import Select, { components } from "react-select";

function CustomSelect({
  options = [],
  onChange,
  value,
  placeholder = "Select...",
  isDisabled = false,
  label,
  helperText,
  error,
  icon,
  isClearable = false,
  isSearchable = true,
  isMulti = false,
  loading = false,
  ...props
}) {
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderColor: error ? "#ef4444" : state.isFocused ? "#1F4D2C" : "#d1d5db",
      borderRadius: "8px",
      boxShadow: state.isFocused
        ? error
          ? "0 0 0 3px rgba(239, 68, 68, 0.1)"
          : "0 0 0 3px rgba(31, 77, 44, 0.1)"
        : "none",
      "&:hover": {
        borderColor: error
          ? "#ef4444"
          : state.isFocused
            ? "#1F4D2C"
            : "#9ca3af",
      },
      backgroundColor: state.isDisabled ? "#f9fafb" : "#ffffff",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
      fontFamily: "Inter, sans-serif",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 12px",
      fontFamily: "Inter, sans-serif",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      fontFamily: "Inter, sans-serif",
      fontSize: "0.9rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "0.9rem",
      fontFamily: "Inter, sans-serif",
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? "#9ca3af" : "#374151",
      fontSize: "0.9rem",
      fontFamily: "Inter, sans-serif",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#f0fdf4",
      borderRadius: "6px",
      border: "1px solid #bbf7d0",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#166534",
      fontSize: "0.85rem",
      fontFamily: "Inter, sans-serif",
      padding: "2px 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#166534",
      "&:hover": {
        backgroundColor: "#dcfce7",
        color: "#166534",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isDisabled ? "#d1d5db" : "#6b7280",
      padding: "8px",
      transition: "all 0.2s ease",
      "&:hover": {
        color: state.isDisabled ? "#d1d5db" : "#1F4D2C",
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      padding: "8px",
      cursor: "pointer",
      "&:hover": {
        color: "#ef4444",
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e5e7eb",
      marginTop: "4px",
      overflow: "hidden",
      fontFamily: "Inter, sans-serif",
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: "4px",
      fontFamily: "Inter, sans-serif",
      maxHeight: "300px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#1F4D2C"
        : state.isFocused
          ? "#f0fdf4"
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#374151",
      padding: "10px 12px",
      cursor: "pointer",
      borderRadius: "6px",
      fontSize: "0.9rem",
      fontFamily: "Inter, sans-serif",
      transition: "all 0.15s ease",
      "&:active": {
        backgroundColor: state.isSelected ? "#1F4D2C" : "#dcfce7",
      },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "0.9rem",
      padding: "12px",
      fontFamily: "Inter, sans-serif",
    }),
    loadingMessage: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "0.9rem",
      padding: "12px",
      fontFamily: "Inter, sans-serif",
    }),
  };

  // Custom Dropdown Indicator with Icon
  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {icon ? (
          <i className={icon} style={{ fontSize: "1rem" }}></i>
        ) : (
          <i className="bi bi-chevron-down" style={{ fontSize: "0.85rem" }}></i>
        )}
      </components.DropdownIndicator>
    );
  };

  return (
    <div style={{ position: "relative", width: "75%" }}>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: error ? "#ef4444" : "#374151",
            marginBottom: "6px",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {label}
        </label>
      )}
      <Select
        options={options}
        onChange={onChange}
        value={value}
        placeholder={placeholder}
        isDisabled={isDisabled}
        // isClearable={isClearable}
        isSearchable={isSearchable}
        isMulti={isMulti}
        isLoading={loading}
        styles={customStyles}
        components={{ DropdownIndicator }}
        noOptionsMessage={() => "No options available"}
        loadingMessage={() => "Loading..."}
        {...props}
      />
      {helperText && !error && (
        <small
          style={{
            display: "block",
            marginTop: "4px",
            color: "#6b7280",
            fontSize: "0.8rem",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <i className="bi bi-info-circle me-1"></i>
          {helperText}
        </small>
      )}
      {error && (
        <small
          style={{
            display: "block",
            marginTop: "4px",
            color: "#ef4444",
            fontSize: "0.8rem",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <i className="bi bi-exclamation-circle me-1"></i>
          {error}
        </small>
      )}
    </div>
  );
}

export default CustomSelect;
